var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import * as d3 from 'd3';
import Typography from '../Typography';
import { formatNumberAsCurrency, getUniqueKey } from '../utils';
import { LabelsContentWrapperStyled, InnerGraphInfoCircleStyled, LabelColorContainerStyled, GraphContentWrapperStyled, LabelContainerStyled, WrapperStyled, } from './styled';
const CircularGraph = (_a) => {
    var _b, _c, _d;
    var props = __rest(_a, []);
    const { initialLabelType, finalLabelType, hideLabels, data, content, size, labelFontSize, labelPadding, total, innerProps, chartId, } = props;
    const [totalData, setTotalData] = useState(0);
    useEffect(() => {
        if (!total) {
            setTotalData(data.reduce((acc, { value }) => acc + value, 0));
        }
        else {
            setTotalData(total);
        }
        const spacing = data.length > 1 ? 0.05 : 0;
        const dataValues = data.map(({ value }) => value);
        const colorMap = data.map(({ color }) => color);
        const width = size;
        const height = size;
        const radius = Math.min(width, height) / 2;
        d3.select(`#${chartId !== null && chartId !== void 0 ? chartId : 'donut-chart'}`)
            .selectAll('*')
            .remove();
        const svg = d3
            .select(`#${chartId !== null && chartId !== void 0 ? chartId : 'donut-chart'}`)
            .style('position', 'relative')
            .style('width', '100%')
            .style('height', '100%')
            .append('svg')
            .style('position', 'absolute')
            .style('top', '50%')
            .style('left', '50%')
            .style('transform', 'translate(-50%, -50%)')
            .attr('width', width)
            .attr('height', height)
            .style('overflow', 'visible')
            .append('g')
            .attr('transform', `translate(${width / 2},${height / 2})`);
        const arc = d3
            .arc()
            .innerRadius(radius * 0.8)
            .outerRadius(radius * 1)
            .cornerRadius(100);
        const pie = d3.pie().value((d) => d);
        const g = svg
            .selectAll('.arc')
            .data(pie(dataValues))
            .enter()
            .append('g')
            .attr('data-testid', (_, i) => `test-group-${i}`)
            .attr('class', 'arc');
        const getDArg = (d, currentArc) => {
            const startAngle = d.startAngle + spacing;
            const endAngle = d.endAngle - spacing;
            const newArc = d3
                .arc()
                .innerRadius(currentArc.innerRadius())
                .outerRadius(currentArc.outerRadius())
                .startAngle(startAngle)
                .endAngle(endAngle)
                .cornerRadius(100);
            return newArc(d);
        };
        g.append('path')
            .attr('d', (d) => getDArg(d, arc))
            .style('fill', (_, i) => colorMap[i])
            .style('filter', 'drop-shadow(4px 4px 20px rgba(111, 140, 176, 0.41)) drop-shadow(-6px -6px 20px #FFF) drop-shadow(2px 2px 4px rgba(114, 142, 171, 0.10))');
        const largeArcCentroid = (d) => {
            const newArc = d3
                .arc()
                .innerRadius(radius * 1.3)
                .outerRadius(radius * 1.2)
                .startAngle(d.startAngle)
                .endAngle(d.endAngle)
                .cornerRadius(100);
            return newArc.centroid(d);
        };
        const labels = g
            .append('foreignObject')
            .attr('class', 'label')
            .attr('data-testid', (_, i) => `test-label-${i}`)
            .style('display', 'none')
            .attr('x', '-50%')
            .attr('y', '-50%')
            .attr('width', '100%')
            .attr('height', '100%')
            .style('position', 'absolute')
            .style('top', '0')
            .style('left', '0')
            .style('z-index', '3')
            .style('pointer-events', 'none')
            .style('transform', 'translate(0%, 0%)')
            .style('overflow', 'visible');
        const labelWrapper = labels
            .append('xhtml:div')
            .style('position', 'absolute')
            .style('top', '50%')
            .style('left', '50%')
            .style('transform', 'translate(-50%, -50%)');
        const labelBox = labelWrapper
            .append('div')
            .style('transform', (d) => {
            return `translate(${largeArcCentroid(d)[0]}px, ${largeArcCentroid(d)[1]}px)`;
        })
            .style('border-radius', '6px')
            .style('border', '1px solid #D6E3F3')
            .style('background', '#E3EDF7')
            .style('box-shadow', '4px 4px 20px 0px rgba(111, 140, 176, 0.41), -6px -6px 20px 0px #FFF, 2px 2px 4px 0px rgba(114, 142, 171, 0.10)')
            .style('display', 'flex')
            .style('align-items', 'center')
            .style('width', 'min-content')
            .style('height', 'min-content')
            .style('justify-content', 'center');
        labelBox
            .append('span')
            .style('padding', labelPadding !== null && labelPadding !== void 0 ? labelPadding : '6px 8px')
            .style('font-size', labelFontSize !== null && labelFontSize !== void 0 ? labelFontSize : '13px')
            .style('font-weight', '500')
            .style('color', '#557FC3')
            .style('font-family', 'Poppins')
            .style('line-height', '20px')
            .text((d) => `${initialLabelType !== null && initialLabelType !== void 0 ? initialLabelType : ''}${formatNumberAsCurrency(d.data)}${finalLabelType !== null && finalLabelType !== void 0 ? finalLabelType : ''}`);
        g.on('mouseover', function mouseOverHandler() {
            d3.select(this).select('.label').style('display', 'block');
        }).on('mouseout', function mouseOutHandler() {
            d3.select(this).select('.label').style('display', 'none');
        });
    }, [props]);
    return (_jsxs(WrapperStyled, { children: [_jsxs(GraphContentWrapperStyled, Object.assign({ size: `${size}px` }, { children: [_jsx("div", { id: `${chartId !== null && chartId !== void 0 ? chartId : 'donut-chart'}` }), _jsxs(InnerGraphInfoCircleStyled, Object.assign({ size: `${size * 0.6}px` }, { children: [(innerProps === null || innerProps === void 0 ? void 0 : innerProps.textPosition) === 'top' && (_jsx(Typography, Object.assign({ style: {
                                    textAlign: 'center',
                                    fontSize: `${(_b = innerProps === null || innerProps === void 0 ? void 0 : innerProps.textSize) !== null && _b !== void 0 ? _b : 16}px`,
                                    lineHeight: 1.2,
                                } }, { children: content || '' }))), _jsxs(Typography, Object.assign({ style: { fontSize: `${(_c = innerProps === null || innerProps === void 0 ? void 0 : innerProps.numberSize) !== null && _c !== void 0 ? _c : 30}px` } }, { children: [initialLabelType, formatNumberAsCurrency(totalData) || '', finalLabelType] })), (innerProps === null || innerProps === void 0 ? void 0 : innerProps.textPosition) === 'bottom' && (_jsx(Typography, Object.assign({ style: {
                                    textAlign: 'center',
                                    fontSize: `${(_d = innerProps === null || innerProps === void 0 ? void 0 : innerProps.textSize) !== null && _d !== void 0 ? _d : 16}px`,
                                    lineHeight: 1.2,
                                } }, { children: content || '' })))] }))] })), !hideLabels && (_jsx(LabelsContentWrapperStyled, Object.assign({ "data-testid": 'labels-title', width: `${size}px` }, { children: data.map(({ label, color }) => (_jsxs(LabelContainerStyled, { children: [_jsx(LabelColorContainerStyled, { backgroundColor: color }), _jsx(Typography, Object.assign({ color: "#2D2F39" }, { children: label }))] }, getUniqueKey()))) })))] }));
};
CircularGraph.defaultProps = {
    size: 243,
    hideLabels: false,
    innerProps: {
        textPosition: 'bottom',
        textSize: 20,
        numberSize: 30,
    },
};
export default CircularGraph;
//# sourceMappingURL=index.js.map